/**
 * The Appendix's Glossary tab — §2.8 #88.
 *
 * Extracted from `ReferenceViewer`, where it was inline JSX sharing the
 * parent's `searchTerm` state with nothing else. The search is local and
 * client-side: the term list for a lesson is small and already loaded, so
 * filtering it is a substring match over `term` and `definition` rather than a
 * round trip.
 *
 * Terms are listed A-Z, and can be narrowed by category with a row of chips
 * (each with its count) - every term carries a category, and a long glossary
 * had no way to show only, say, the enzymes. The category is a quiet label
 * beside the term rather than a coloured pill competing with it, and each
 * example reads as a quotation, not a form field labelled "Examples:".
 *
 * A term that names one of the lesson's concepts carries that concept's
 * anchor picture (`visual`), shown as a small thumbnail beside the definition:
 * the word and the picture together, the way the rest of the lesson shows it.
 *
 * Both fields are searched, deliberately. A learner who remembers "the thing
 * about the thylakoid" but not the term's name has only the definition to go
 * on, and a term-only filter would return nothing.
 *
 * `isLoading` is separate from an empty list because the two look the same and
 * mean opposite things: `StepGate` has already decided the glossary step is
 * `completed` by the time this renders, but the `/glossary` content query can
 * still be in flight — showing "No terms found" there tells the user the
 * lesson has no glossary when it is simply not back yet.
 */
import { useMemo, useState } from 'react';
import { Input, EmptyState, cn } from '@ai-tutor/ui';
import { BookOpen, Search } from 'lucide-react';
import {
  BlockMarkdown,
  InlineMarkdown,
} from '@/components/core/markdown/markdown';
import { EnlargeableThumb, hasThumb } from '@/components/core/visual';
import type { Visual } from '@/types/visual';
import { GlossarySkeleton } from '../skeletons/PanelSkeletons';

/** A glossary term, in the shape both the API and mock paths normalise to. */
export interface GlossaryTerm {
  id: string;
  term: string;
  definition: string;
  category: string;
  examples?: string[];
  /** The anchor picture of the concept the term names, if any. */
  visual?: Visual | null;
}

export interface GlossaryPanelProps {
  terms: GlossaryTerm[];
  /** Current search text. Controlled, because `ReferenceViewer` owns it. */
  searchTerm: string;
  onSearchChange: (value: string) => void;
  /** True while the content query is in flight — NOT "the list is empty". */
  isLoading?: boolean;
}

/** Substring match over the term AND its definition. */
export function filterGlossaryTerms(
  terms: GlossaryTerm[],
  searchTerm: string
): GlossaryTerm[] {
  const q = searchTerm.toLowerCase();
  return terms.filter(
    term =>
      term.term.toLowerCase().includes(q) ||
      term.definition.toLowerCase().includes(q)
  );
}

/** Categories by how many terms they hold, most first; ties A-Z. */
function categoryCounts(terms: GlossaryTerm[]): [string, number][] {
  const counts = new Map<string, number>();
  for (const t of terms)
    counts.set(t.category, (counts.get(t.category) ?? 0) + 1);
  return [...counts.entries()].sort(
    (a, b) => b[1] - a[1] || a[0].localeCompare(b[0])
  );
}

export function GlossaryPanel({
  terms,
  searchTerm,
  onSearchChange,
  isLoading,
}: GlossaryPanelProps) {
  const [category, setCategory] = useState<string | null>(null);
  const categories = useMemo(() => categoryCounts(terms), [terms]);
  const filtered = filterGlossaryTerms(terms, searchTerm)
    .filter(t => category === null || t.category === category)
    .sort((a, b) => a.term.localeCompare(b.term));

  return (
    <div className='flex h-full flex-col'>
      <div className='space-y-3 p-4 pb-2'>
        <div className='relative'>
          <Search
            className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground'
            aria-hidden='true'
          />
          <Input
            placeholder='Search terms...'
            aria-label='Search glossary terms'
            value={searchTerm}
            onChange={e => onSearchChange(e.target.value)}
            // Transparent like the rest of the panel: the input's own
            // recessed fill read as a second surface on the lesson panel.
            className='bg-transparent pl-10 text-foreground ![box-shadow:none]'
          />
        </div>
        {categories.length > 1 && (
          <CategoryChips
            total={terms.length}
            categories={categories}
            active={category}
            onChange={setCategory}
          />
        )}
      </div>

      <div className='flex-1 overflow-y-auto px-4 pb-4'>
        {isLoading ? (
          <GlossarySkeleton />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={BookOpen}
            title='No terms found'
            description='Try adjusting your search or filter criteria'
          />
        ) : (
          // Below `sm`: rows divided by a hairline on the lesson's paper, not
          // a box per term - three boxed terms filled a phone screen.
          <div className='space-y-3 max-sm:space-y-0 max-sm:divide-y max-sm:divide-border'>
            {filtered.map(term => (
              <GlossaryEntry key={term.id} term={term} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryChips({
  total,
  categories,
  active,
  onChange,
}: {
  total: number;
  categories: [string, number][];
  active: string | null;
  onChange: (category: string | null) => void;
}) {
  const chip = (label: string, count: number, value: string | null) => {
    const on = active === value;
    return (
      <button
        key={label}
        type='button'
        aria-pressed={on}
        onClick={() => onChange(value)}
        className={cn(
          'tap-target flex h-8 flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-sm transition-colors',
          on
            ? 'border-foreground bg-foreground text-background'
            : 'border-border text-foreground hover:bg-muted'
        )}
      >
        {label}
        <span
          className={cn(
            'font-mono text-xs',
            on ? 'text-background/70' : 'text-muted-foreground'
          )}
        >
          {count}
        </span>
      </button>
    );
  };
  return (
    // Scrolls sideways rather than wrapping: a glossary can have many
    // categories, and two lines of chips would push the terms down.
    <div
      role='group'
      aria-label='Filter by category'
      className='-mx-4 flex gap-2 overflow-x-auto px-4 py-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
    >
      {chip('All', total, null)}
      {categories.map(([name, count]) => chip(name, count, name))}
    </div>
  );
}

function GlossaryEntry({ term }: { term: GlossaryTerm }) {
  return (
    <article className='rounded-lg border border-border/60 p-4 max-sm:rounded-none max-sm:border-0 max-sm:px-0 max-sm:py-3.5'>
      <div className='flex items-baseline justify-between gap-3'>
        <h4 className='text-[15px] font-semibold text-foreground sm:text-base'>
          <InlineMarkdown>{term.term}</InlineMarkdown>
        </h4>
        <span className='flex-shrink-0 font-mono text-xs text-muted-foreground'>
          {term.category}
        </span>
      </div>
      <div className='mt-1 flex items-start gap-3'>
        <BlockMarkdown className='min-w-0 flex-1 text-sm leading-relaxed text-foreground/80'>
          {term.definition}
        </BlockMarkdown>
        {/* A picture, or - for a term without one - its concept's diagram,
            shrunk to a preview that opens full size. */}
        {term.visual &&
          (hasThumb(term.visual) || term.visual.kind === 'diagram') && (
            <EnlargeableThumb
              visual={term.visual}
              label={term.term}
              className='h-16 w-16 flex-shrink-0'
              diagram
            />
          )}
      </div>
      {term.examples && term.examples.length > 0 && (
        <ul className='mt-2 space-y-1' aria-label={`Examples of ${term.term}`}>
          {term.examples.map((example, index) => (
            <li
              key={index}
              className='border-l-2 border-border pl-3 text-sm italic text-muted-foreground'
            >
              <InlineMarkdown>{example}</InlineMarkdown>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}
